import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import type { paths } from '../types/prove-v1'

export type ElvenettRiver =
  paths['/geo/elvenett']['get']['responses'][200]['content']['application/json'][number]

type AuthFetch = ReturnType<typeof useAuth>['authFetch']
const ELVENETT_TIMEOUT_MS = 10_000

export const isValidWgs84Coordinate = (
  lon?: number | null,
  lat?: number | null
): boolean => {
  if (lon == null || lat == null) return false
  if (typeof lon !== 'number' || typeof lat !== 'number') return false
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
}

export const getRiversNearPointQueryOptions = (
  authFetch: AuthFetch,
  lon?: number | null,
  lat?: number | null,
  enabled: boolean = true
) => {
  const canFetch = enabled && isValidWgs84Coordinate(lon, lat)

  return queryOptions({
    queryKey: ['elvenett', lon, lat],
    queryFn: canFetch
      ? async ({ signal }) => {
          const timeoutController = new AbortController()
          const timeoutId = setTimeout(
            () => timeoutController.abort('timeout'),
            ELVENETT_TIMEOUT_MS
          )
          const handleAbort = () => timeoutController.abort(signal.reason)
          if (signal.aborted) {
            handleAbort()
          } else {
            signal.addEventListener('abort', handleAbort, { once: true })
          }

          try {
            const { data, error } = await authFetch().GET('/geo/elvenett', {
              params: {
                query: {
                  lon: lon!,
                  lat: lat!
                }
              },
              signal: timeoutController.signal
            })

            if (error) {
              throw new Error('Kunne ikke hente elver i nærheten')
            }

            return data ?? []
          } finally {
            clearTimeout(timeoutId)
            signal.removeEventListener('abort', handleAbort)
          }
        }
      : skipToken
  })
}

export const useGetRiversNearPointQuery = (
  lon?: number | null,
  lat?: number | null,
  options?: { enabled?: boolean }
) => {
  const { authFetch } = useAuth()
  const enabled = options?.enabled ?? true
  return useQuery(getRiversNearPointQueryOptions(authFetch, lon, lat, enabled))
}
